/***
*
*   HELP
*   The help centre, embedded in the app shell, plus the support surfaces that
*   have always lived here: the support-access consent toggle and the message
*   form that reaches the support mailbox.
*
*   /help shows the embed home, /help/:slug opens a named article, and the
*   address bar follows the frame so any article can be linked or bookmarked.
*
*   Two rules hold this together:
*
*   1. The frame is rebuilt by changing its React `key`, never by assigning to a
*      live frame's `src` – an assignment pushes an entry onto the *joint*
*      session history, so back would need two presses per step.
*   2. Mirroring the frame's route into the address bar always *replaces*. The
*      frame's own SPA navigation already contributes the history entries; a
*      push here would double them.
*
*   Nothing identifying crosses the boundary: no email, name, account or token
*   is ever passed inward, in the url or in a message. When the frame asks for
*   support it gets the app's own form, which posts to an authenticated endpoint
*   that reads the sender from the session – the form itself carries no identity.
*
**********/

import React, { Fragment, useCallback, useContext, useEffect,
  useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ViewContext, Card, Form, Message, useAPI, useNavigate,
  useHelpFrame, ClassHelper } from 'components/lib';
import { HELP_EMBED_ENABLED, normalizeSlug, helpPath, helpEmbedUrl,
  helpPublicUrl } from 'components/help/help';
import { Faqs } from './faqs';
import Style from './help.tailwind.js';

// the frame runs from its own top edge to the bottom of the viewport. the
// offset above it moves (notices mount and unmount), so it is measured, never
// hardcoded as a calc()
const MIN_FRAME_HEIGHT = 480;
const FRAME_BOTTOM_GAP = 24;

export function Help(props){

  // config kill switch: flips /help back to the FAQ page without a code change
  return HELP_EMBED_ENABLED ?
    <EmbeddedHelp { ...props }/> :
    <FaqHelp { ...props }/>;

}

/***
*   EmbeddedHelp
*   The help centre in an iframe, with the support surfaces below it.
**********/

function EmbeddedHelp(props){

  const context = useContext(ViewContext);
  const navigate = useNavigate();
  const params = useParams();
  const user = useAPI('/api/user');

  // a malformed slug resolves to null here and never reaches the frame src
  const urlSlug = normalizeSlug(params.slug);

  const frameRef = useRef(null);
  const wrapperRef = useRef(null);

  // the article the frame says it is showing. compared against the url slug so
  // a route we mirrored back into the address bar doesn't rebuild the frame
  const frameSlugRef = useRef(urlSlug);
  const articleRef = useRef({ slug: urlSlug, title: null });

  const [frame, setFrame] = useState(() => ({ key: 1, src: helpEmbedUrl(urlSlug) }));
  const [frameHeight, setFrameHeight] = useState(MIN_FRAME_HEIGHT);

  /* the frame reported a route – follow it in the address bar */
  const handleRoute = useCallback(({ slug, title }) => {

    articleRef.current = { slug: slug, title: title };

    // record it before navigating, so the rebuild effect below sees a match
    // and leaves the live frame alone
    frameSlugRef.current = slug;

    // replace, never push: the frame's own navigation already made the entry
    if (slug !== urlSlug)
      navigate(helpPath(slug), { replace: true });

  }, [navigate, urlSlug]);

  /* the frame asked for support – answer with our own form, not its modal */
  const handleSupportRequest = useCallback(({ slug, title }) => {

    // one at a time: a second request while the modal is open is ignored
    if (context.modal.data?.show)
      return;

    // nothing is gated on /api/user any more: the support endpoint reads the
    // sender from the session, so the form no longer carries an identity to
    // wait for and the modal opens straight away

    const article = {

      slug: slug || articleRef.current.slug,
      title: title || articleRef.current.title,

    };

    // every key is passed explicitly – hiding the modal doesn't clear form,
    // callback or destructive, so a stale one would otherwise ride along
    context.modal.show({

      title: 'Contact Support',
      text: 'Your message goes straight to our support team, who will reply by email.',
      form: supportForm(article),
      url: '/api/support/ticket',
      method: 'POST',
      buttonText: 'Send Message',
      destructive: false,

    });

  }, [context]);

  const { status, resolved, onFrameLoad } = useHelpFrame({

    frameRef: frameRef,
    enabled: true,
    onRoute: handleRoute,
    onSupportRequest: handleSupportRequest,

  });

  /* the url changed to an article the frame isn't showing – rebuild it.
     changing the key gives a fresh browsing context; assigning to src would
     push onto the joint history instead */
  useEffect(() => {

    if (frameSlugRef.current === urlSlug)
      return;

    frameSlugRef.current = urlSlug;
    setFrame(f => ({ key: f.key + 1, src: helpEmbedUrl(urlSlug) }));

  }, [urlSlug]);

  const measure = useCallback(() => {

    const node = wrapperRef.current;
    if (!node) return;

    const viewport = window.visualViewport?.height || window.innerHeight;
    const available = Math.floor(viewport - node.getBoundingClientRect().top - FRAME_BOTTOM_GAP);
    const next = Math.max(MIN_FRAME_HEIGHT, available);

    setFrameHeight(h => h === next ? h : next);

  }, []);

  // re-measure whenever the offset above the frame can have moved: a notice
  // mounting or unmounting shifts it just as a resize does
  useLayoutEffect(() => { measure() }, [measure, resolved, status]);

  useEffect(() => {

    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);

    return () => {

      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);

    }
  }, [measure]);

  const frameWrapStyle = ClassHelper(Style, {

    frameWrap: true,
    connecting: status === 'connecting',

  });

  return (
    <Fragment>

      { !resolved &&
        <Message
          type='warning'
          title='Article not found'
          text="We couldn't find that help article – it may have been renamed or moved. You can browse the help centre, or send us a message below."
          buttonText='Back to the help centre'
          buttonLink='/help'
        />
      }

      { status === 'unavailable' &&
        <Message
          type='warning'
          title="The help centre didn't load"
          text='It may be temporarily unavailable, or blocked by your browser or network. You can open it in a new tab, or send us a message below.'
        >
          <p>
            <a
              className={ Style.noticeLink }
              href={ helpPublicUrl(urlSlug) }
              target='_blank'
              rel='noopener noreferrer'>
              Open the help centre in a new tab
            </a>
          </p>
        </Message>
      }

      <div className={ Style.toolbar }>
        <span className={ Style.toolbarText }>
          Search or browse the help centre below.
        </span>
        <a
          className={ Style.toolbarLink }
          href={ helpPublicUrl(urlSlug) }
          target='_blank'
          rel='noopener noreferrer'>
          Open in a new tab
        </a>
      </div>

      <Card noPadding>
        <div
          ref={ wrapperRef }
          className={ frameWrapStyle }
          style={{ height: `${frameHeight}px` }}>

          <iframe
            key={ frame.key }
            ref={ frameRef }
            src={ frame.src }
            title='IdentityCheck help centre'
            className={ Style.frame }
            referrerPolicy='no-referrer'
            sandbox='allow-scripts allow-same-origin allow-forms allow-popups'
            onLoad={ onFrameLoad }
          />
        </div>
      </Card>

      <SupportCard user={ user.data } slug={ urlSlug }/>

    </Fragment>
  );
}

/***
*   FaqHelp
*   The pre-embed page, kept intact behind the config flag.
**********/

function FaqHelp(props){

  const user = useAPI('/api/user');

  return (
    <Fragment>

      <Message
        title='Need Help?'
        type='info'
        text="Contact our support team below. You can also enable or disable permission for our support team to access your account to help resolve any issues."
      />

      <Card>
        <Faqs />
      </Card>

      <SupportCard user={ user.data }/>

    </Fragment>
  );
}

/***
*   SupportCard
*   The support-access consent toggle and the message form. The message form
*   posts to /api/support/ticket, which emails support and raises a HubSpot
*   ticket carrying the sender's identity and account.
**********/

function SupportCard({ user, slug }){

  // the card still waits on /api/user, but only for the consent toggle's
  // default – the message form no longer carries an identity. Form seeds from
  // props.data exactly once and never re-seeds without updateOnChange, so a
  // toggle mounted before its value arrived would show the wrong answer
  return (
    <Card title='Contact Support' restrictWidth loading={ !user }>

      <Form
        data={{
          support_enabled: {
            type: 'radio',
            options: ['Yes', 'No'],
            required: true,
            errorMessage: 'Please enter a message',
            default: user?.support_enabled ? 'Yes' : 'No',
            label: 'Can our support team access your account?',
          }
        }}
        submitOnChange
        method='PATCH'
        url='/api/user'
      />

      {/* the article rides on the url, never in a field: this card stays mounted
          for the whole session and Form seeds its state only once, so any field
          would freeze at whichever article was open when the card mounted and
          then shadow the real one. props.url is read at submit time, so it
          follows the frame. supportForm() is called with no article for the same
          reason – a stale seeded message is worse than none */}
      <Form
        data={ supportForm() }
        method='POST'
        url={ '/api/support/ticket' + (slug ? `?article=${encodeURIComponent(slug)}` : '') }
        buttonText='Send Message'
      />

    </Card>
  );
}

/* the support message form. no identity fields: the endpoint is authenticated
   and reads the sender from the session, so an email or name posted from here
   would only ever be a spoofing surface. the article is sent as its own field
   as well as being seeded into the message, so the ticket can link it */
function supportForm(article){

  return {

    article_slug: {
      type: 'hidden',
      value: article?.slug || '',
    },
    article_title: {
      type: 'hidden',
      value: article?.title || '',
    },
    message: {
      type: 'textarea',
      label: 'What can we help with?',
      errorMessage: 'Please enter a message',
      required: true,

      // a textarea renders props.value with no empty-string fallback, so it
      // always needs a seed or react warns about an uncontrolled input
      value: articleContext(article),
    }
  }
}

/* the article the user was reading, as a line of the message body */
function articleContext(article){

  const label = article?.title || article?.slug;

  if (!label)
    return '';

  return `Re: ${label} (${helpPublicUrl(article.slug)})\n\n`;

}
